const { GTM_EVENTS, STORAGE } = require('../analyticsConst');
const { PAGE_NAMES } = require('../../../../../config/analytics');

/**
 * A hit is a single entry of the DataLayer array. This object contain useful
 * data for setting the page info of a hit or the global gaInfo
 *
 * @typedef {object} HitPageInfo
 * @property {string} pageName - The name of the page where the hit occurs
 * @property {string} pageType - The type of the page where the hit occurs
 * @property {string} pageUrl - The URL of the page where the hit occurs
 */

/**
 * Get the hit page info from the global `gaInfo`.
 * @returns {HitPageInfo} The page info from the session storage or datalayer
 */
function getCurrentHitPageInfo() {
  /** @type {HitPageInfo} */
  const hitPageInfo = {
    pageUrl: gaInfo.pageUrl,
    pageName: gaInfo.pageName,
    pageType: gaInfo.pageType,
  };
  return hitPageInfo;
}

/**
 * It looks for the most recent page view event in the dataLayer and returns
 * the page info object.
 * @returns {HitPageInfo} The HitPageInfo of the latest pageview hit.
 */
function getHitPageInfoFromDataLayer() {
  /** @type {HitPageInfo} */
  const result = {};

  const pageViewHit = dataLayer.find(
    (hit) => (hit.event === GTM_EVENTS.pageView && hit.pageName !== PAGE_NAMES.search),
  );

  if (pageViewHit) {
    result.pageName = pageViewHit.pageName;
    result.pageUrl = pageViewHit.pageUrl;
    result.pageType = pageViewHit.page_type;
  }

  return result;
}

/**
 * Get the page info from the session storage. If there is no info in session,
 * returns null.
 * @returns {Object|null} The HitPageInfo stored in session
 */
function getHitPageInfoFromSession() {
  /** @type {HitPageInfo} */
  let hitPageInfo;
  try {
    hitPageInfo = JSON.parse(sessionStorage.getItem(STORAGE.session.hitPageInfo));
  } catch {
    hitPageInfo = null;
  }
  return hitPageInfo;
}

/**
 * It gets the hit page info from the session storage, if it exists. Otherwise
 * it gets that info from last pageview hit registered in the datalayer.
 * @returns {HitPageInfo} The page info from the session storage or datalayer
 */
function getPreviousHitPageInfo() {
  const hit = getHitPageInfoFromSession() || getHitPageInfoFromDataLayer();
  return hit;
}

/**
 * Erase the page info from session
 */
function removeHitPageInfoFromSession() {
  sessionStorage.removeItem(STORAGE.session.hitPageInfo);
}

/**
 * It takes the curremt page information from the global variable `gaInfo` and
 * stores it in session storage
 */
function saveHitPageInfoIntoSession() {
  if (!sessionStorage.getItem(STORAGE.session.hit)) {
    const hitPageInfo = getCurrentHitPageInfo();
    sessionStorage.setItem(
      STORAGE.session.hitPageInfo,
      JSON.stringify(hitPageInfo),
    );
  }
}

module.exports = {
  getPreviousHitPageInfo,
  removeHitPageInfoFromSession,
  saveHitPageInfoIntoSession,
};
